import { yupResolver } from '@hookform/resolvers/yup';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ConfirmationModal } from 'src/components/modal/ConfirmationModal';
import { SharedFormProvider } from 'src/components/shared/FormProvider';
import { SidePanel } from 'src/components/sidePanel/SidePanel';
import { setNotice } from 'src/redux/noticesSlice';
import * as yup from 'yup';
import { handlePasteToForm, isObjectEmpty } from '../../../helpers';
import {
  IProvider,
  useUpdateAvatarMutation,
  useUpdateProviderMutation,
} from '../../../redux/services/provider';
import { ProviderHeader } from '../ProviderHeader';
import { IEditProviderProps } from './EditProvider';
import styles from './EditProvider.module.css';
import { ENoticeType } from 'src/enums';

const schema = yup.object({
  name: yup
    .string()
    .required('Обязательное поле')
    .max(50, 'Название не может превышать 50 символов')
    .matches(/[^ ]+/, {
      message: 'Название не может состоять только из пробелов',
    })
    .matches(/^[^ ]+( *[^ ]+)*?$/, 'Название не может содержать пробелы в начале и конце'),
  description: yup
    .string()
    .max(255, 'Описание не может превышать 255 символов')
    .matches(/^$|[^ ]+/, {
      message: 'Описание не может состоять только из пробелов',
    }),
  password_required: yup.boolean(),
  is_public: yup.boolean(),
});

export const EditEthereumProvider: FC<IEditProviderProps> = ({ isOpen, onClose, provider }) => {
  const methods = useForm<IProvider>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { dirtyFields },
    reset,
    control,
  } = methods;

  const dispatch = useDispatch();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [updateProvider, updateResult] = useUpdateProviderMutation();
  const [updateAvatar] = useUpdateAvatarMutation();

  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  useEffect(() => {
    if (updateResult.isSuccess) onClose();
  }, [updateResult]);

  useEffect(() => {
    if (provider && isOpen) {
      reset(provider as IProvider);
    }
  }, [isOpen]);

  const closeSaveModal = () => setSaveModalOpen(false);

  const handleClose = () => {
    if (isObjectEmpty(dirtyFields)) onClose();
    else setSaveModalOpen(true);
  };

  const pasteFields = async () => {
    try {
      await handlePasteToForm(methods);
    } catch (err) {
      dispatch(
        setNotice({
          type: ENoticeType.error,
          id: Math.random(),
          isRead: false,
          message: `Ошибка вставки параметров: ${err}`,
          timestamp: new Date().toString(),
        }),
      );
    }
  };

  const onSubmit: SubmitHandler<IProvider> = (data) => {
    updateProvider(data).then(() => {
      setTimeout(() => {
        updateAvatar({
          clientId: data.client_id,
          providerId: data.id,
          avatar: uploadedAvatar,
        });
      }, 1000);
    });
  };

  return (
    <SidePanel
      onClose={handleClose}
      isOpen={isOpen}
      title="Редактировать способ входа Ethereum"
      AdditionalAction={() => pasteFields()}
      customAdditionalText="Вставить настройки"
      CustomAdditionalIcon={ContentPasteGoOutlinedIcon}
      isNoBackdrop
    >
      <SharedFormProvider<IProvider>
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        handleCancel={handleClose}
        isDisabled={updateResult.isLoading || isObjectEmpty(dirtyFields)}
      >
        <div className={styles['create-provider-form']}>
          <ProviderHeader />
        </div>
      </SharedFormProvider>

      <ConfirmationModal
        title="Сохранение изменений"
        mainMessage={['Изменения не сохранены. Продолжить без сохранения?']}
        actionButtonText="Продолжить"
        isOpen={saveModalOpen}
        onAction={() => {
          onClose();
          setSaveModalOpen(false);
        }}
        onClose={closeSaveModal}
      />
    </SidePanel>
  );
};
